import { MovementPhase } from './movementData'
import { timePointToDay } from './bulkFuelData'

// Types for combat assets
export type CombatVehicleType = 'mrap' | 'gun_truck'
export type WeaponType = 'm2' | 'mk19' | 'm240'
export type ReadinessStatus = 'green' | 'amber' | 'red'

// Interface for daily combat slant status
export interface DailyCombatSlantStatus {
  day: number
  combatPowerPercentage: number
  securityVehicles: {
    mraps: number
    gunTrucks: number
  }
  weaponsLoadPercentage: number
  readinessStatus: ReadinessStatus
  notes: string
}

// Interface for combat vehicles
export interface CombatVehicle {
  id: string
  name: string
  type: CombatVehicleType
  position: 'lead' | 'midpoint' | 'trail'
  coordinates: [number, number] // [latitude, longitude]
  weapons: WeaponType[]
  day: number
  isOperational: boolean
}

// Interface for security sector coverage
export interface SecuritySectorCoverage {
  id: string
  vehicleId: string
  day: number
  centerCoordinates: [number, number] // [latitude, longitude]
  radiusMeters: number // Coverage radius in meters
  coverageAngle: {
    start: number // in degrees (0-360)
    end: number // in degrees (0-360)
  }
}

// Status colors for visual representation
export const readinessStatusColors: Record<ReadinessStatus, string> = {
  green: '#059669', // Green
  amber: '#D97706', // Amber
  red: '#DC2626', // Red
}

// Vehicle type colors
export const combatVehicleColors: Record<CombatVehicleType, string> = {
  mrap: '#1E40AF', // Blue
  gun_truck: '#7C3AED', // Purple
}

// Weapon type information
export const weaponTypeInfo: Record<
  WeaponType,
  { name: string; description: string }
> = {
  m2: {
    name: 'M2 .50 Cal',
    description: 'Browning M2 .50 caliber heavy machine gun',
  },
  mk19: {
    name: 'Mk19',
    description: 'Mk19 40mm automatic grenade launcher',
  },
  m240: {
    name: 'M240',
    description: 'M240 7.62mm medium machine gun',
  },
}

// Daily combat slant status data
export const dailyCombatSlantStatus: DailyCombatSlantStatus[] = [
  {
    day: 1,
    combatPowerPercentage: 100,
    securityVehicles: {
      mraps: 4,
      gunTrucks: 2,
    },
    weaponsLoadPercentage: 100,
    readinessStatus: 'green',
    notes: 'All systems green',
  },
  {
    day: 2,
    combatPowerPercentage: 98,
    securityVehicles: {
      mraps: 4,
      gunTrucks: 2,
    },
    weaponsLoadPercentage: 90,
    readinessStatus: 'green',
    notes: 'Minor ammo use in drills',
  },
  {
    day: 3,
    combatPowerPercentage: 95,
    securityVehicles: {
      mraps: 3,
      gunTrucks: 2,
    },
    weaponsLoadPercentage: 80,
    readinessStatus: 'green',
    notes: 'One MRAP down for maintenance',
  },
  {
    day: 4,
    combatPowerPercentage: 93,
    securityVehicles: {
      mraps: 3,
      gunTrucks: 2,
    },
    weaponsLoadPercentage: 70,
    readinessStatus: 'amber',
    notes: 'Ammo low – pending Class V resupply',
  },
  {
    day: 5,
    combatPowerPercentage: 100,
    securityVehicles: {
      mraps: 4,
      gunTrucks: 2,
    },
    weaponsLoadPercentage: 100,
    readinessStatus: 'green',
    notes: 'Full reload at Fort Stockton',
  },
  {
    day: 6,
    combatPowerPercentage: 98,
    securityVehicles: {
      mraps: 4,
      gunTrucks: 2,
    },
    weaponsLoadPercentage: 85,
    readinessStatus: 'green',
    notes: 'Normal drawdown from security halts',
  },
  {
    day: 7,
    combatPowerPercentage: 97,
    securityVehicles: {
      mraps: 4,
      gunTrucks: 2,
    },
    weaponsLoadPercentage: 75,
    readinessStatus: 'green',
    notes: 'Sufficient firepower to secure arrival objectives',
  },
]

// Combat vehicles data
export const combatVehicles: CombatVehicle[] = [
  // Day 1 - Presidio to Fort Stockton
  {
    id: 'mrap-lead-day1',
    name: 'MRAP Alpha (Lead)',
    type: 'mrap',
    position: 'lead',
    coordinates: [29.5607, -104.3729], // Presidio
    weapons: ['m2', 'm240'],
    day: 1,
    isOperational: true,
  },
  {
    id: 'mrap-mid1-day1',
    name: 'MRAP Bravo (Mid)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [30.0, -104.2], // Between Presidio and Marfa
    weapons: ['mk19'],
    day: 1,
    isOperational: true,
  },
  {
    id: 'gun-mid2-day1',
    name: 'Gun Truck Charlie (Mid)',
    type: 'gun_truck',
    position: 'midpoint',
    coordinates: [30.2, -104.1], // Between Presidio and Marfa
    weapons: ['m240'],
    day: 1,
    isOperational: true,
  },
  {
    id: 'mrap-trail1-day1',
    name: 'MRAP Delta (Trail)',
    type: 'mrap',
    position: 'trail',
    coordinates: [30.3094, -104.0177], // Marfa
    weapons: ['m2'],
    day: 1,
    isOperational: true,
  },
  {
    id: 'gun-trail2-day1',
    name: 'Gun Truck Echo (Trail)',
    type: 'gun_truck',
    position: 'trail',
    coordinates: [30.35, -104.0], // Just past Marfa
    weapons: ['m240'],
    day: 1,
    isOperational: true,
  },
  {
    id: 'mrap-flank-day1',
    name: 'MRAP Foxtrot (Flank)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [30.6, -103.4], // Between Marfa and Fort Stockton
    weapons: ['mk19', 'm240'],
    day: 1,
    isOperational: true,
  },
  // Day 2 - Fort Stockton to Sanderson
  {
    id: 'mrap-lead-day2',
    name: 'MRAP Alpha (Lead)',
    type: 'mrap',
    position: 'lead',
    coordinates: [30.8941, -102.8793], // Fort Stockton
    weapons: ['m2', 'm240'],
    day: 2,
    isOperational: true,
  },
  {
    id: 'mrap-mid1-day2',
    name: 'MRAP Bravo (Mid)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [30.6, -102.7], // Between Fort Stockton and Sanderson
    weapons: ['mk19'],
    day: 2,
    isOperational: true,
  },
  {
    id: 'gun-mid2-day2',
    name: 'Gun Truck Charlie (Mid)',
    type: 'gun_truck',
    position: 'midpoint',
    coordinates: [30.5, -102.6], // Between Fort Stockton and Sanderson
    weapons: ['m240'],
    day: 2,
    isOperational: true,
  },
  {
    id: 'mrap-trail1-day2',
    name: 'MRAP Delta (Trail)',
    type: 'mrap',
    position: 'trail',
    coordinates: [30.3, -102.5], // Between Fort Stockton and Sanderson
    weapons: ['m2'],
    day: 2,
    isOperational: true,
  },
  {
    id: 'gun-trail2-day2',
    name: 'Gun Truck Echo (Trail)',
    type: 'gun_truck',
    position: 'trail',
    coordinates: [30.25, -102.45], // Between Fort Stockton and Sanderson
    weapons: ['m240'],
    day: 2,
    isOperational: true,
  },
  {
    id: 'mrap-flank-day2',
    name: 'MRAP Foxtrot (Flank)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [30.2, -102.4], // Near Sanderson
    weapons: ['mk19', 'm240'],
    day: 2,
    isOperational: true,
  },
  // Day 3 - Sanderson to Del Rio
  {
    id: 'mrap-lead-day3',
    name: 'MRAP Alpha (Lead)',
    type: 'mrap',
    position: 'lead',
    coordinates: [30.1422, -102.3979], // Sanderson
    weapons: ['m2', 'm240'],
    day: 3,
    isOperational: true,
  },
  {
    id: 'mrap-mid1-day3',
    name: 'MRAP Bravo (Mid)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [29.8, -101.8], // Between Sanderson and Del Rio
    weapons: ['mk19'],
    day: 3,
    isOperational: false, // Down for maintenance
  },
  {
    id: 'gun-mid2-day3',
    name: 'Gun Truck Charlie (Mid)',
    type: 'gun_truck',
    position: 'midpoint',
    coordinates: [29.7, -101.7], // Between Sanderson and Del Rio
    weapons: ['m240'],
    day: 3,
    isOperational: true,
  },
  {
    id: 'mrap-trail1-day3',
    name: 'MRAP Delta (Trail)',
    type: 'mrap',
    position: 'trail',
    coordinates: [29.5, -101.3], // Between Sanderson and Del Rio
    weapons: ['m2'],
    day: 3,
    isOperational: true,
  },
  {
    id: 'gun-trail2-day3',
    name: 'Gun Truck Echo (Trail)',
    type: 'gun_truck',
    position: 'trail',
    coordinates: [29.45, -101.25], // Between Sanderson and Del Rio
    weapons: ['m240'],
    day: 3,
    isOperational: true,
  },
  {
    id: 'mrap-flank-day3',
    name: 'MRAP Foxtrot (Flank)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [29.4, -101.0], // Near Del Rio
    weapons: ['mk19', 'm240'],
    day: 3,
    isOperational: true,
  },
  // Day 4 - Del Rio to Eagle Pass
  {
    id: 'mrap-lead-day4',
    name: 'MRAP Alpha (Lead)',
    type: 'mrap',
    position: 'lead',
    coordinates: [29.3627, -100.8968], // Del Rio
    weapons: ['m2', 'm240'],
    day: 4,
    isOperational: true,
  },
  {
    id: 'mrap-mid1-day4',
    name: 'MRAP Bravo (Mid)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [29.1, -100.7], // Between Del Rio and Eagle Pass
    weapons: ['mk19'],
    day: 4,
    isOperational: false, // Still down for maintenance
  },
  {
    id: 'gun-mid2-day4',
    name: 'Gun Truck Charlie (Mid)',
    type: 'gun_truck',
    position: 'midpoint',
    coordinates: [29.0, -100.65], // Between Del Rio and Eagle Pass
    weapons: ['m240'],
    day: 4,
    isOperational: true,
  },
  {
    id: 'mrap-trail1-day4',
    name: 'MRAP Delta (Trail)',
    type: 'mrap',
    position: 'trail',
    coordinates: [28.9, -100.6], // Between Del Rio and Eagle Pass
    weapons: ['m2'],
    day: 4,
    isOperational: true,
  },
  {
    id: 'gun-trail2-day4',
    name: 'Gun Truck Echo (Trail)',
    type: 'gun_truck',
    position: 'trail',
    coordinates: [28.85, -100.58], // Between Del Rio and Eagle Pass
    weapons: ['m240'],
    day: 4,
    isOperational: true,
  },
  {
    id: 'mrap-flank-day4',
    name: 'MRAP Foxtrot (Flank)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [28.8, -100.55], // Near Eagle Pass
    weapons: ['mk19', 'm240'],
    day: 4,
    isOperational: true,
  },
  // Day 5 - Eagle Pass to Laredo (after resupply)
  {
    id: 'mrap-lead-day5',
    name: 'MRAP Alpha (Lead)',
    type: 'mrap',
    position: 'lead',
    coordinates: [28.7091, -100.4995], // Eagle Pass
    weapons: ['m2', 'm240'],
    day: 5,
    isOperational: true,
  },
  {
    id: 'mrap-mid1-day5',
    name: 'MRAP Bravo (Mid)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [28.4, -100.2], // Between Eagle Pass and Laredo
    weapons: ['mk19'],
    day: 5,
    isOperational: true, // Repaired and back in service
  },
  {
    id: 'gun-mid2-day5',
    name: 'Gun Truck Charlie (Mid)',
    type: 'gun_truck',
    position: 'midpoint',
    coordinates: [28.3, -100.1], // Between Eagle Pass and Laredo
    weapons: ['m240'],
    day: 5,
    isOperational: true,
  },
  {
    id: 'mrap-trail1-day5',
    name: 'MRAP Delta (Trail)',
    type: 'mrap',
    position: 'trail',
    coordinates: [28.1, -99.9], // Between Eagle Pass and Laredo
    weapons: ['m2'],
    day: 5,
    isOperational: true,
  },
  {
    id: 'gun-trail2-day5',
    name: 'Gun Truck Echo (Trail)',
    type: 'gun_truck',
    position: 'trail',
    coordinates: [28.05, -99.85], // Between Eagle Pass and Laredo
    weapons: ['m240'],
    day: 5,
    isOperational: true,
  },
  {
    id: 'mrap-flank-day5',
    name: 'MRAP Foxtrot (Flank)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [27.8, -99.6], // Near Laredo
    weapons: ['mk19', 'm240'],
    day: 5,
    isOperational: true,
  },
  // Day 6 - Laredo to McAllen
  {
    id: 'mrap-lead-day6',
    name: 'MRAP Alpha (Lead)',
    type: 'mrap',
    position: 'lead',
    coordinates: [27.5306, -99.4803], // Laredo
    weapons: ['m2', 'm240'],
    day: 6,
    isOperational: true,
  },
  {
    id: 'mrap-mid1-day6',
    name: 'MRAP Bravo (Mid)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [27.2, -99.0], // Between Laredo and McAllen
    weapons: ['mk19'],
    day: 6,
    isOperational: true,
  },
  {
    id: 'gun-mid2-day6',
    name: 'Gun Truck Charlie (Mid)',
    type: 'gun_truck',
    position: 'midpoint',
    coordinates: [27.1, -98.9], // Between Laredo and McAllen
    weapons: ['m240'],
    day: 6,
    isOperational: true,
  },
  {
    id: 'mrap-trail1-day6',
    name: 'MRAP Delta (Trail)',
    type: 'mrap',
    position: 'trail',
    coordinates: [26.8, -98.6], // Between Laredo and McAllen
    weapons: ['m2'],
    day: 6,
    isOperational: true,
  },
  {
    id: 'gun-trail2-day6',
    name: 'Gun Truck Echo (Trail)',
    type: 'gun_truck',
    position: 'trail',
    coordinates: [26.75, -98.55], // Between Laredo and McAllen
    weapons: ['m240'],
    day: 6,
    isOperational: true,
  },
  {
    id: 'mrap-flank-day6',
    name: 'MRAP Foxtrot (Flank)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [26.5, -98.4], // Near McAllen
    weapons: ['mk19', 'm240'],
    day: 6,
    isOperational: true,
  },
  // Day 7 - McAllen to Brownsville
  {
    id: 'mrap-lead-day7',
    name: 'MRAP Alpha (Lead)',
    type: 'mrap',
    position: 'lead',
    coordinates: [26.2033, -98.23], // McAllen
    weapons: ['m2', 'm240'],
    day: 7,
    isOperational: true,
  },
  {
    id: 'mrap-mid1-day7',
    name: 'MRAP Bravo (Mid)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [26.1, -98.0], // Between McAllen and Brownsville
    weapons: ['mk19'],
    day: 7,
    isOperational: true,
  },
  {
    id: 'gun-mid2-day7',
    name: 'Gun Truck Charlie (Mid)',
    type: 'gun_truck',
    position: 'midpoint',
    coordinates: [26.05, -97.95], // Between McAllen and Brownsville
    weapons: ['m240'],
    day: 7,
    isOperational: true,
  },
  {
    id: 'mrap-trail1-day7',
    name: 'MRAP Delta (Trail)',
    type: 'mrap',
    position: 'trail',
    coordinates: [26.0, -97.8], // Between McAllen and Brownsville
    weapons: ['m2'],
    day: 7,
    isOperational: true,
  },
  {
    id: 'gun-trail2-day7',
    name: 'Gun Truck Echo (Trail)',
    type: 'gun_truck',
    position: 'trail',
    coordinates: [25.95, -97.75], // Between McAllen and Brownsville
    weapons: ['m240'],
    day: 7,
    isOperational: true,
  },
  {
    id: 'mrap-flank-day7',
    name: 'MRAP Foxtrot (Flank)',
    type: 'mrap',
    position: 'midpoint',
    coordinates: [25.92, -97.5], // Near Brownsville
    weapons: ['mk19', 'm240'],
    day: 7,
    isOperational: true,
  },
]

// Security sector coverage data
export const securitySectorCoverage: SecuritySectorCoverage[] = [
  // Day 1 coverage
  {
    id: 'sector-lead-day1',
    vehicleId: 'mrap-lead-day1',
    day: 1,
    centerCoordinates: [29.5607, -104.3729], // Presidio
    radiusMeters: 1000,
    coverageAngle: {
      start: 0,
      end: 120,
    },
  },
  {
    id: 'sector-mid-day1',
    vehicleId: 'mrap-mid1-day1',
    day: 1,
    centerCoordinates: [30.0, -104.2], // Between Presidio and Marfa
    radiusMeters: 1000,
    coverageAngle: {
      start: 90,
      end: 270,
    },
  },
  {
    id: 'sector-trail-day1',
    vehicleId: 'mrap-trail1-day1',
    day: 1,
    centerCoordinates: [30.3094, -104.0177], // Marfa
    radiusMeters: 1000,
    coverageAngle: {
      start: 180,
      end: 300,
    },
  },
  // Similar entries for other days would follow...
]

// Helper function to get combat slant status for a specific day
export const getCombatSlantStatusForDay = (
  day: number,
): DailyCombatSlantStatus => {
  return (
    dailyCombatSlantStatus.find((status) => status.day === day) ||
    dailyCombatSlantStatus[0]
  )
}

// Helper function to get combat vehicles for a specific day
export const getCombatVehiclesForDay = (day: number): CombatVehicle[] => {
  return combatVehicles.filter((vehicle) => vehicle.day === day)
}

// Helper function to get security sector coverage for a specific day
export const getSecuritySectorCoverageForDay = (
  day: number,
): SecuritySectorCoverage[] => {
  return securitySectorCoverage.filter((sector) => sector.day === day)
}
